import { ACKNOWLEDGEMENT_LOOKBACK_DAYS } from "@storyboard/dispatch-view/acknowledgement";
import type {
  Acknowledgement,
  AcknowledgementLogQuery,
  AcknowledgementPage,
  AcknowledgementSubject,
} from "@storyboard/web-client";
import { listAcknowledgementLog, serverNow } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { ACKNOWLEDGEMENT_SUBJECTS, upsertAcknowledgements } from "@/models/acknowledgement/acknowledgementStore";
import {
  coverageKey,
  coverageStoreMigratingFrom,
  registryCoverageStore,
  registryCursorStore,
} from "@/models/sync/cursorStore";
import {
  browserPollerClock,
  COLD_BOOT_LADDER_HOURS,
  type CoverageStore,
  createCoverageSync,
  startPolling,
} from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { nextTmsEventPollDelayMs } from "@/models/tmsEvent/tmsEventPoller";
import { recordEvent } from "@/services/telemetry";

const log = createLogger("appsync");

/** Rows per page, the most the route serves. */
export const ACKNOWLEDGEMENT_PAGE_LIMIT = 500;

/** Pages one gap fill may take before it stops; its progress is kept, and the next sync resumes from it. */
export const ACKNOWLEDGEMENT_MAX_DELTA_PAGES = 50;

/** How far back a subject's log is held: the acknowledgement lookback, past which an item is handled by age. */
export const ACKNOWLEDGEMENT_DEPTH = { hours: ACKNOWLEDGEMENT_LOOKBACK_DAYS * 24 } as const;

/** A subject read through the coverage engine; `task` is walked by the task sync, from the cursor-paged log. */
export type DeltaAcknowledgementSubject = Exclude<AcknowledgementSubject, "task">;

/** The subjects read through the coverage engine, in the vocabulary's order. */
export const DELTA_ACKNOWLEDGEMENT_SUBJECTS: readonly DeltaAcknowledgementSubject[] = ACKNOWLEDGEMENT_SUBJECTS.filter(
  (subject): subject is DeltaAcknowledgementSubject => subject !== "task",
);

/** The log every subject's coverage is keyed under. */
const ACKNOWLEDGEMENT_LOG = "acknowledgements";

/** Registry key of a subject's legacy cursor, read once to seed its coverage; never written any more. */
export function acknowledgementCursorKey(subject: AcknowledgementSubject): string {
  return `acknowledgements:${subject}Cursor`;
}

/** Registry key of a subject's coverage. */
export function acknowledgementCoverageKey(subject: AcknowledgementSubject): string {
  return coverageKey(ACKNOWLEDGEMENT_LOG, subject);
}

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type AcknowledgementSyncDeps = {
  fetchPage: (query: AcknowledgementLogQuery) => Promise<AcknowledgementPage>;
  coverage: CoverageStore;
  apply: (rows: readonly Acknowledgement[]) => void;
  report: (name: string, attributes: Record<string, unknown>) => void;
  /** The server's clock as the client estimates it from every response's `Date` header. */
  now: () => Date;
};

/**
 * Binds the coverage engine to the acknowledgement logs.
 *
 * Each subject is one log on the `createdAt` axis, held to the lookback. The engine decides what to fetch, newest
 * first; this binding decides how a page is fetched and landed. Nothing older than the lookback is read: an item past
 * it counts as handled by age, so no acknowledgement of it changes the fold.
 */
export function createAcknowledgementSync(deps: AcknowledgementSyncDeps) {
  const engine = createCoverageSync<DeltaAcknowledgementSubject, AcknowledgementPage>({
    depth: ACKNOWLEDGEMENT_DEPTH,
    ladderHours: COLD_BOOT_LADDER_HOURS,
    maxPages: ACKNOWLEDGEMENT_MAX_DELTA_PAGES,
    store: deps.coverage,
    keyOf: acknowledgementCoverageKey,
    fetchPage: (ctx, subject, since, cursor) =>
      deps.fetchPage({
        workspaceId: ctx.workspaceId,
        subject,
        limit: ACKNOWLEDGEMENT_PAGE_LIMIT,
        since: since.toISOString(),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    applyPage: (page) => deps.apply(page.acknowledgements),
    // Rows come oldest first, so the page's last row is its newest.
    newestCreatedAt: (page) => {
      const newest = page.acknowledgements.at(-1)?.createdAt;
      return newest === undefined ? undefined : new Date(newest);
    },
    now: deps.now,
    report: (event, subject, attributes) =>
      deps.report(`acknowledgements.${event}`, { acknowledgement: { subject, ...attributes } }),
  });

  return { walkDelta: engine.sync, backfilled: engine.backfilled };
}

const production = createAcknowledgementSync({
  fetchPage: (query) => listAcknowledgementLog(transport, query),
  coverage: coverageStoreMigratingFrom(
    registryCoverageStore,
    registryCursorStore,
    new Map(
      DELTA_ACKNOWLEDGEMENT_SUBJECTS.map((subject) => [
        acknowledgementCoverageKey(subject),
        acknowledgementCursorKey(subject),
      ]),
    ),
    ACKNOWLEDGEMENT_DEPTH,
    serverNow,
  ),
  apply: upsertAcknowledgements,
  report: (name, attributes) => recordEvent(name, attributes, "reliability"),
  now: serverNow,
});

/**
 * Brings a subject's log up to date: the rows created since its newest held span, or on a cold boot the recent
 * window first and the rest of the lookback in the background.
 */
export function walkAcknowledgementDelta(ctx: SyncContext, subject: DeltaAcknowledgementSubject): Promise<void> {
  return production.walkDelta(ctx, subject);
}

/** Walks every delta subject's log; a subject that fails is logged and left to its next poll. */
export async function walkAllAcknowledgements(ctx: SyncContext): Promise<void> {
  await Promise.all(
    DELTA_ACKNOWLEDGEMENT_SUBJECTS.map(async (subject) => {
      try {
        await walkAcknowledgementDelta(ctx, subject);
      } catch (error) {
        log.warn("[sync] acknowledgement walk failed; the next poll retries", { subject, error });
      }
    }),
  );
}

/**
 * Walks every subject now and then on the TMS events' cadence for as long as the workspace is open.
 *
 * The poll is the floor under the ping, so a colleague's acknowledgement is never more than about a minute late. A
 * hidden tab lets its ticks pass and walks once when shown again.
 *
 * @returns `stop` ends the poller, safe to call more than once; `bootstrapped` resolves once the first walk of every
 *   subject and the lookback behind it have landed, and never rejects, so a boot can hold on it.
 */
export function startAcknowledgementSyncInBrowser(ctx: SyncContext): { stop: () => void; bootstrapped: Promise<void> } {
  const bootstrapped = walkAllAcknowledgements(ctx).then(() =>
    Promise.all(DELTA_ACKNOWLEDGEMENT_SUBJECTS.map((subject) => production.backfilled(ctx, subject))).then(
      () => undefined,
    ),
  );
  const stop = startPolling(() => walkAllAcknowledgements(ctx), browserPollerClock(nextTmsEventPollDelayMs));
  return { stop, bootstrapped };
}
